import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, MapPin, ArrowRight, Check, PartyPopper } from "lucide-react";
import { api } from "@/lib/api";
import { GooseLogo, Spinner, FadeIn } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO = "https://images.unsplash.com/photo-1544785316-6e58aed68a50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function BadgeReveal({ number, name }) {
  return (
    <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 220, damping: 16 }}
      className="goose-card relative overflow-hidden p-8 text-center" data-testid="badge-reveal">
      <div className="absolute -inset-10 bg-rose-500/20 blur-3xl" />
      <div className="relative">
        <PartyPopper className="mx-auto h-8 w-8 text-amber-400" />
        <p className="mt-4 text-sm text-slate-400">You're on the list{name ? `, ${name.split(" ")[0]}` : ""}. Your Goose number:</p>
        <p className="font-mono-g mt-3 text-6xl font-black tracking-widest text-rose-500 glow-rose">#{number}</p>
        <p className="mt-4 text-xs text-slate-500">Bring this with you. On event night you'll verify by email and look up compatibility by badge number.</p>
      </div>
    </motion.div>
  );
}

export default function GuestAttribution() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // 'survey' | 'already'
  const [form, setForm] = useState({ name: "", email: "" });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: event, isLoading, error } = useQuery({
    queryKey: ["pub-event", slug],
    queryFn: async () => (await api.get(`/public/events/${slug}`)).data,
  });

  if (isLoading) return <div className="goose-grain min-h-screen"><Spinner /></div>;
  if (error) return (
    <div className="flex min-h-screen items-center justify-center goose-grain px-6 text-center">
      <div><GooseLogo className="text-3xl" /><p className="mt-4 text-slate-400">This event link is not valid.</p></div>
    </div>
  );

  const startSurvey = async () => {
    if (!form.email) { toast.error("Enter your email"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/public/events/${slug}/start-survey`, { email: form.email, name: form.name });
      navigate(`/survey/${slug}?e=${encodeURIComponent(form.email)}&n=${data.goose_number}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setSaving(false); }
  };

  const already = async () => {
    if (!form.email) { toast.error("Enter your email"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/public/events/${slug}/already-completed`, { email: form.email });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setSaving(false); }
  };

  return (
    <div className="goose-grain min-h-screen">
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col px-4 py-8">
        <GooseLogo className="text-xl" onClick={() => navigate("/")} />

        <FadeIn className="mt-6">
          <div className="relative overflow-hidden rounded-3xl border border-slate-800">
            <img src={HERO} alt="" className="h-44 w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#090A0F] to-transparent" />
            <div className="absolute bottom-4 left-5 right-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-rose-400">You're invited</p>
              <h1 className="font-display mt-1 text-2xl font-bold leading-tight" data-testid="event-name">{event.name}</h1>
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.08} className="mt-4">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400">
            <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-slate-600" /> {event.date} · {event.start_time}</span>
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-600" /> {event.location}</span>
          </div>
        </FadeIn>

        <div className="mt-8 flex-1">
          <AnimatePresence mode="wait">
            {result ? (
              <BadgeReveal key="reveal" number={result.goose_number} name={result.name} />
            ) : mode === null ? (
              <FadeIn key="choices">
                <p className="text-sm text-slate-400">To join the Goose experience, complete the HAEVN compatibility survey — or tell us you already have.</p>
                <div className="mt-5 space-y-3">
                  <button data-testid="choice-survey-btn" onClick={() => setMode("survey")}
                    className="goose-card goose-card-hover flex w-full items-center justify-between p-5 text-left">
                    <div><p className="font-display text-lg font-semibold">Complete the survey</p><p className="text-sm text-slate-400">Takes a couple of minutes</p></div>
                    <ArrowRight className="h-5 w-5 text-rose-400" />
                  </button>
                  <button data-testid="choice-already-btn" onClick={() => setMode("already")}
                    className="goose-card goose-card-hover flex w-full items-center justify-between p-5 text-left">
                    <div><p className="font-display text-lg font-semibold">I've already completed it</p><p className="text-sm text-slate-400">Match me by my HAEVN email</p></div>
                    <Check className="h-5 w-5 text-emerald-400" />
                  </button>
                </div>
              </FadeIn>
            ) : (
              <FadeIn key="form">
                <div className="goose-card p-6">
                  <p className="font-display text-lg font-semibold">{mode === "survey" ? "Start the survey" : "Match me to this event"}</p>
                  <div className="mt-4 space-y-4">
                    {mode === "survey" && (
                      <div><Label className="mb-1.5 block text-xs text-slate-400">Your name</Label>
                        <Input data-testid="guest-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Alex" /></div>
                    )}
                    <div><Label className="mb-1.5 block text-xs text-slate-400">{mode === "survey" ? "Email" : "Your HAEVN email"}</Label>
                      <Input data-testid="guest-email-input" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@email.com" /></div>
                  </div>
                  <Button data-testid="guest-continue-btn" disabled={saving} onClick={mode === "survey" ? startSurvey : already}
                    className="btn-pill mt-5 w-full bg-rose-500 py-6 font-bold hover:bg-rose-400">
                    {saving ? "Please wait…" : mode === "survey" ? "Continue to survey" : "Match me"}
                  </Button>
                  <button onClick={() => setMode(null)} className="mt-3 w-full text-center text-xs text-slate-500 hover:text-slate-300">Back</button>
                </div>
              </FadeIn>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
